<script context="module" lang="ts">
  export type Testimonial =
    | { quote: string; attribution: string }
    /** A proof figure shown as a slide, e.g. "81%" + what it measures. */
    | { stat: string; text: string };
</script>

<script lang="ts">
  import { onMount } from "svelte";

  export let testimonials: Testimonial[] = [];
  /** Auto-advance interval; 0 disables it. Also disabled under prefers-reduced-motion. */
  export let intervalMs = 8000;
  /** Hero-sized: smaller type and controls so it sits under a CTA. */
  export let compact = false;

  let index = 0;
  // Timers exist only in the browser after mount: this component is prerendered
  // (adapter-static), and a setInterval started during SSR would never be cleared.
  let mounted = false;
  let paused = false;
  /** The visitor's explicit choice; wins over hover/focus pausing either way. */
  let stopped = false;
  let reducedMotion = false;
  let timer: ReturnType<typeof setInterval> | null = null;

  $: count = testimonials.length;
  $: autoplay = mounted && intervalMs > 0 && count > 1 && !reducedMotion && !paused && !stopped;

  const show = (next: number) => {
    index = ((next % count) + count) % count;
  };
  const advance = () => show(index + 1);

  const restartTimer = () => {
    if (timer) clearInterval(timer);
    timer = autoplay ? setInterval(advance, intervalMs) : null;
  };
  $: (autoplay, restartTimer());

  const togglePlay = () => {
    stopped = !stopped;
  };

  // Pause for hover and for keyboard focus, but not for a mouse click on the
  // controls: the button keeps focus after the click and would freeze the rotation.
  const onFocusIn = (event: FocusEvent) => {
    const target = event.target as HTMLElement | null;
    paused = Boolean(target?.matches?.(":focus-visible"));
  };

  onMount(() => {
    mounted = true;
    // jsdom has no matchMedia; treat that as "no preference".
    if (typeof window.matchMedia !== "function") return () => timer && clearInterval(timer);
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    reducedMotion = query.matches;
    const onChange = (event: MediaQueryListEvent) => (reducedMotion = event.matches);
    query.addEventListener("change", onChange);
    return () => {
      query.removeEventListener("change", onChange);
      if (timer) clearInterval(timer);
    };
  });
</script>

{#if count > 0}
  <div
    class="carousel relative"
    role="group"
    aria-roledescription="carousel"
    aria-label="What learners say"
    on:mouseenter={() => (paused = true)}
    on:mouseleave={() => (paused = false)}
    on:focusin={onFocusIn}
    on:focusout={() => (paused = false)}>
    <!-- All slides share one grid cell so the band keeps the tallest quote's
         height and never jumps; only the current one is visible or announced.
         The pause control sits top-right so it takes no vertical space. -->
    {#if count > 1 && intervalMs > 0 && !reducedMotion}
      <button
        type="button"
        class="control"
        on:click={togglePlay}
        aria-pressed={stopped}
        aria-label={stopped ? "Play quotes" : "Pause quotes"}>
        {#if stopped}
          <svg aria-hidden="true" viewBox="0 0 20 20" class="h-4 w-4" fill="currentColor">
            <path d="M7 4.5v11l8-5.5-8-5.5z" />
          </svg>
        {:else}
          <svg aria-hidden="true" viewBox="0 0 20 20" class="h-4 w-4" fill="currentColor">
            <rect x="5.5" y="4.5" width="3" height="11" rx="0.75" />
            <rect x="11.5" y="4.5" width="3" height="11" rx="0.75" />
          </svg>
        {/if}
      </button>
    {/if}
    <div class="slides" aria-live={autoplay ? "off" : "polite"} aria-atomic="true">
      {#each testimonials as testimonial, i}
        {#if "stat" in testimonial}
          <div class="slide" class:is-current={i === index} aria-hidden={i !== index}>
            <p class="font-bold tracking-tight text-white {compact ? 'text-4xl' : 'text-5xl sm:text-6xl'}">
              {testimonial.stat}
            </p>
            <p
              class="max-w-[30ch] text-balance font-semibold leading-snug text-white {compact ? 'mt-1' : 'mt-2'}"
              style={compact ? "font-size: 1.0625rem" : "font-size: clamp(1.25rem, 1.2vw + 0.9rem, 1.75rem)"}>
              {testimonial.text}
            </p>
            <p class="sr-only">Slide {i + 1} of {count}</p>
          </div>
        {:else}
          <blockquote class="slide" class:is-current={i === index} aria-hidden={i !== index}>
            <p
              class="max-w-[38ch] text-balance font-semibold leading-snug text-white"
              style={compact ? "font-size: 1.0625rem" : "font-size: clamp(1.25rem, 1.2vw + 0.9rem, 1.75rem)"}>
              “{testimonial.quote}”
            </p>
            <footer class="text-sm text-brand-tint {compact ? 'mt-2' : 'mt-3'}">
              <span class="font-semibold text-white">{testimonial.attribution}</span>
              <span class="sr-only">, slide {i + 1} of {count}</span>
            </footer>
          </blockquote>
        {/if}
      {/each}
    </div>
  </div>
{/if}

<style>
  .slides {
    display: grid;
    padding-right: 3rem;
  }

  .slide {
    grid-area: 1 / 1;
    visibility: hidden;
    opacity: 0;
  }

  .slide.is-current {
    visibility: visible;
    opacity: 1;
    animation: slide-in 320ms cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  @keyframes slide-in {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .control {
    position: absolute;
    top: 0;
    right: 0;
    display: inline-flex;
    height: 44px;
    width: 44px;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    border: 1px solid rgb(255 255 255 / 0.7);
    border-radius: 9999px;
    color: #ffffff;
    transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
  }

  .control:hover {
    background-color: rgb(255 255 255 / 0.12);
  }

  .control:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    .slide.is-current {
      animation: none;
    }
  }
</style>
